"use client";

/**
 * Residence photo with a designed fallback. The admin-hosted image URL
 * can point at a file that is missing on this host (local dev has the
 * prod URLs but not the prod media), and a bare <img> then renders the
 * browser's broken-image glyph. On error (or with no URL) this renders
 * one of the placeholder scenes instead of a gradient tile.
 */
import { useState } from "react";
import Illo from "@/components/Illo";
import Photo from "@/components/Photo";
import { mediaSrc } from "@/lib/api";

// Three placeholder scenes (public/illustrations/residence, made by
// ops/scripts/gen_finder_illustrations.py --set residence); the pick is
// stable per residence so a photo-less list does not repeat one picture
// and a card never flips between them.
const PLACEHOLDERS = 3;
export function placeholderFor(seed: string): string {
  // Mixed with a large odd constant: a plain 31-polynomial put names that
  // sort together on the same variant, so a list showed runs of one scene.
  let h = 0x811c9dc5;
  for (const ch of seed) h = Math.imul(h ^ ch.charCodeAt(0), 0x9e3779b1) >>> 0;
  return `/illustrations/residence/placeholder_${((h >>> 8) % PLACEHOLDERS) + 1}`;
}

// Every caller shows the photo card-sized (<=~260 css px), while the
// uploaded originals are full-size (Grand Hayat's was 236 KB). The media
// route resizes on `?w=` (max 512, enough for a 2x screen) and the edge
// caches each width.
const CARD_WIDTH = 512;

function sized(url: string, width: number): string {
  if (!url.includes("/api/media/complexes/") || /[?&]w=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}w=${width}`;
}

export default function ResidenceImage({
  src,
  fallbackSrc = null,
  alt,
  className,
  imgClassName = "",
  iconClassName = "w-8 h-8",
  loading = "lazy",
  width = CARD_WIDTH,
}: {
  src?: string | null;
  /** Tried when `src` fails, before the placeholder scene (a listing
   *  site's photo behind a hosted hero that is missing on this host). */
  fallbackSrc?: string | null;
  alt: string;
  /** Classes for the frame (size, radius, position). */
  className: string;
  /** Extra classes for the <img> itself (hover zoom etc.). */
  imgClassName?: string;
  iconClassName?: string;
  loading?: "lazy" | "eager";
  /** Requested image width in px (2x the shown size); map markers ask for 96. */
  width?: number;
}) {
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(src) && !broken;
  // The frame positions its children absolutely, so it needs to be a
  // containing block itself: relative by default, but never fight a
  // caller that already passes `absolute inset-0` (the showcase does).
  const positioned = /\babsolute\b|\bfixed\b/.test(className);
  return (
    <div className={`${positioned ? "" : "relative"} overflow-hidden ${className}`}>
      {showImage ? (
        // Photo holds a quiet frame, fades the file in, and reports a
        // failure (also one that happened before hydration).
        <Photo
          src={sized(mediaSrc(src!)!, width)}
          fallbackSrc={fallbackSrc ? sized(mediaSrc(fallbackSrc)!, width) : null}
          alt={alt}
          loading={loading}
          className={imgClassName}
          onBroken={() => setBroken(true)}
        />
      ) : (
        <Illo
          base={placeholderFor(alt || src || "")}
          className={`absolute inset-0 h-full w-full object-cover ${imgClassName}`}
        />
      )}
    </div>
  );
}
